import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { api, formatError, formatPrice } from "../../lib/api";
import { AdminTitle, AdminField, AdminCheck, Panel } from "./AdminUi";

const EMPTY = { code: "", type: "percent", value: "", min_amount: 0, max_uses: "", expires_at: "", active: true, description: "" };

export default function AdminPromos() {
  const [promos, setPromos] = useState([]);
  const [f, setF] = useState(null);
  const reqId = useRef(0);
  const load = () => {
    const id = ++reqId.current;
    return api.get("/admin/promos").then((r) => { if (id === reqId.current) setPromos(r.data); });
  };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, value: parseFloat(f.value), min_amount: parseFloat(f.min_amount) || 0,
      max_uses: f.max_uses ? parseInt(f.max_uses, 10) : null, expires_at: f.expires_at || null };
    try {
      if (f.id) await api.put(`/admin/promos/${f.id}`, body); else await api.post("/admin/promos", body);
      toast.success("Code enregistré"); setF(null); load();
    } catch (err) { toast.error(formatError(err)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Supprimer le code ${p.code} ?`)) return;
    await api.delete(`/admin/promos/${p.id}`); load();
  };

  return (
    <div data-testid="admin-promos">
      <AdminTitle title="Codes promo" action={<button className="btn-gold !py-3" onClick={() => setF({ ...EMPTY })} data-testid="admin-add-promo-button"><Plus className="h-4 w-4" />Créer un code</button>}>
        Les codes sont appliqués par le client à l'étape de commande.
      </AdminTitle>
      <Panel className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/45 border-b border-[#C9A45C]/15">
            <th className="p-3">Code</th><th className="p-3">Réduction</th><th className="p-3">Minimum</th><th className="p-3">Utilisations</th><th className="p-3">Expiration</th><th className="p-3">Statut</th><th className="p-3" />
          </tr></thead>
          <tbody>
            {promos.map((p) => (
              <tr key={p.id} className="border-b border-[#C9A45C]/10" data-testid={`admin-promo-row-${p.code}`}>
                <td className="p-3"><p className="text-[#C9A45C] tracking-wider">{p.code}</p>{p.description && <p className="text-xs text-[#F8F6F1]/45">{p.description}</p>}</td>
                <td className="p-3">{p.type === "percent" ? `${p.value} %` : formatPrice(p.value)}</td>
                <td className="p-3">{p.min_amount ? formatPrice(p.min_amount) : "—"}</td>
                <td className="p-3">{p.usage_count}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                <td className="p-3">{p.expires_at || "—"}</td>
                <td className="p-3">{p.active ? "Actif" : "Inactif"}</td>
                <td className="p-3"><div className="flex justify-end gap-1">
                  <button onClick={() => setF({ ...EMPTY, ...p, max_uses: p.max_uses ?? "", expires_at: p.expires_at ?? "" })} className="p-2 hover:text-[#C9A45C]" aria-label="Modifier" data-testid={`admin-edit-promo-${p.code}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(p)} className="p-2 hover:text-red-400" aria-label="Supprimer" data-testid={`admin-delete-promo-${p.code}`}><Trash2 className="h-4 w-4" /></button>
                </div></td>
              </tr>
            ))}
            {!promos.length && <tr><td colSpan={7} className="p-10 text-center text-[#F8F6F1]/45">Aucun code promo.</td></tr>}
          </tbody>
        </table>
      </Panel>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="bg-[#101010] border-[#C9A45C]/30 max-w-lg">
          <DialogTitle className="font-display text-2xl font-normal">{f?.id ? "Modifier le code" : "Nouveau code promo"}</DialogTitle>
          {f && (
            <form onSubmit={save} className="grid sm:grid-cols-2 gap-4" data-testid="promo-form">
              <AdminField label="Code" required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} testid="promo-form-code" />
              <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/50">Type</span>
                <select value={f.type} onChange={set("type")} className="mt-1.5 w-full bg-[#0B0B0B] border border-[#C9A45C]/25 px-3 py-2.5 text-sm" data-testid="promo-form-type">
                  <option value="percent">Pourcentage (%)</option><option value="fixed">Montant fixe (€)</option>
                </select>
              </label>
              <AdminField label="Valeur" type="number" step="0.01" min="0.01" required value={f.value} onChange={set("value")} testid="promo-form-value" />
              <AdminField label="Montant minimum (€)" type="number" step="0.01" min="0" value={f.min_amount} onChange={set("min_amount")} />
              <AdminField label="Utilisations max." type="number" min="1" value={f.max_uses} onChange={set("max_uses")} />
              <AdminField label="Date d'expiration" type="date" value={f.expires_at} onChange={set("expires_at")} />
              <AdminField className="sm:col-span-2" label="Note interne" value={f.description} onChange={set("description")} />
              <AdminCheck label="Actif" checked={f.active} onChange={(v) => setF({ ...f, active: v })} testid="promo-form-active" />
              <div className="sm:col-span-2 flex justify-end gap-3">
                <button type="button" onClick={() => setF(null)} className="btn-outline-gold !py-3">Annuler</button>
                <button type="submit" className="btn-gold !py-3" data-testid="promo-form-submit">Enregistrer</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
